// Reading and writing the user's records, laid out as the phone app lays
// them out (PRD, Data model): everything under users/{uid}, so the security
// rules (firestore.rules) only compare user ids. Writes don't wait for the
// server: Firestore applies them to its cache at once and sends them when
// online, so the app works offline. A refused write is reported to
// [onWriteRefused] listeners, so the app can say the change didn't stick.
import {
  collection,
  deleteDoc,
  doc,
  type DocumentData,
  getDoc,
  getDocFromCache,
  getDocsFromCache,
  increment,
  onSnapshot,
  type QueryDocumentSnapshot,
  runTransaction,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { type Business, businessFromJson, businessToJson, sortBusinesses } from '@/core/business';
import { type Client, clientFromJson, clientToJson, sortClients } from '@/core/client';
import { type Invoice, invoiceFromJson, invoiceToJson, sortInvoices } from '@/core/invoice';
import { FREE_PLAN, type Plan, planFromUserRecord } from '@/core/plan';
import { type SavedItem, savedItemFromJson, savedItemToJson, sortSavedItems } from '@/core/savedItem';
import { firestore } from '@/data/firebase';

type Unsubscribe = () => void;

const refusedListeners = new Set<(error: unknown) => void>();

export function onWriteRefused(listener: (error: unknown) => void): Unsubscribe {
  refusedListeners.add(listener);
  return () => refusedListeners.delete(listener);
}

/** Starts a write without waiting for the server. */
function inBackground(write: Promise<unknown>, what: string) {
  write.catch((error) => {
    console.error(`Could not ${what}`, error);
    refusedListeners.forEach((l) => l(error));
  });
}

const businesses = (uid: string) => collection(firestore(), 'users', uid, 'businesses');
const clients = (uid: string) => collection(firestore(), 'users', uid, 'clients');
const invoices = (uid: string) => collection(firestore(), 'users', uid, 'invoices');
const items = (uid: string) => collection(firestore(), 'users', uid, 'items');

/** Parses each document on its own, skipping any that won't, so one bad record never empties a list. */
function parseDocs<T>(docs: QueryDocumentSnapshot<DocumentData>[], fromJson: (json: Record<string, unknown>) => T): T[] {
  const parsed: T[] = [];
  for (const d of docs) {
    try {
      parsed.push(fromJson(d.data()));
    } catch (error) {
      console.error(`Skipping a document that could not be read: ${d.ref.path}`, error);
    }
  }
  return parsed;
}

// ── Business ─────────────────────────────────────────────────────────────

/** Every business of the user, oldest first (a user can have several). */
export function watchBusinesses(uid: string, onChange: (all: Business[]) => void, onError: (e: unknown) => void): Unsubscribe {
  return onSnapshot(businesses(uid), (s) => onChange(sortBusinesses(parseDocs(s.docs, businessFromJson))), onError);
}

/** A business from the server, or from the browser's copy offline. */
export async function getBusiness(uid: string, businessId: string): Promise<Business | null> {
  let snapshot;
  try {
    snapshot = await getDoc(doc(businesses(uid), businessId));
  } catch {
    snapshot = await getDocFromCache(doc(businesses(uid), businessId));
  }
  const data = snapshot.data();
  return data ? businessFromJson(data) : null;
}

/**
 * The business from the browser's copy only, without waiting for the
 * server: null when the copy has none or can't be read.
 */
export async function getCachedBusinesses(uid: string): Promise<Business[]> {
  try {
    const snapshot = await getDocsFromCache(businesses(uid));
    return sortBusinesses(parseDocs(snapshot.docs, businessFromJson));
  } catch {
    return [];
  }
}

export function saveBusiness(uid: string, business: Business) {
  inBackground(setDoc(doc(businesses(uid), business.id), businessToJson(business)), 'save the business');
}

export function removeBusiness(uid: string, id: string) {
  inBackground(deleteDoc(doc(businesses(uid), id)), `delete business ${id}`);
}

/**
 * Online, a transaction makes the number unique across devices. Offline,
 * the number comes from the local copy and an increment is queued; two
 * devices numbering offline at once could pick the same number, which the
 * user can edit. A transaction that commits after the timeout skips a
 * number, which is harmless.
 */
export async function takeNextInvoiceNumber(uid: string, businessId: string, timeoutMs = 5000): Promise<number> {
  const ref = doc(businesses(uid), businessId);
  const snapshot = await getDoc(ref).catch(() => getDocFromCache(ref));
  if (!snapshot.exists()) throw new Error('Set up the business before numbering invoices.');
  const now = () => new Date().toISOString();
  try {
    const transaction = runTransaction(firestore(), async (tx) => {
      const fresh = await tx.get(ref);
      const number = Math.trunc(Number(fresh.data()?.nextInvoiceNumber ?? 1)) || 1;
      tx.update(ref, { nextInvoiceNumber: number + 1, updatedAt: now() });
      return number;
    });
    const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), timeoutMs));
    return await Promise.race([transaction, timeout]);
  } catch (error) {
    console.warn('Numbering offline', error);
    const number = Math.trunc(Number(snapshot.data()?.nextInvoiceNumber ?? 1)) || 1;
    inBackground(updateDoc(ref, { nextInvoiceNumber: increment(1), updatedAt: now() }), 'reserve an invoice number');
    return number;
  }
}

// ── Invoices ─────────────────────────────────────────────────────────────

/** All invoices, newest first, and which have changes not yet on the server. */
export function watchInvoices(uid: string, onChange: (invoices: Invoice[], unsynced: Set<string>) => void, onError: (e: unknown) => void): Unsubscribe {
  return onSnapshot(
    invoices(uid),
    { includeMetadataChanges: true },
    (s) => onChange(sortInvoices(parseDocs(s.docs, invoiceFromJson)), new Set(s.docs.filter((d) => d.metadata.hasPendingWrites).map((d) => d.id))),
    onError,
  );
}

export function saveInvoice(uid: string, invoice: Invoice) {
  inBackground(setDoc(doc(invoices(uid), invoice.id), invoiceToJson(invoice)), `save invoice ${invoice.id}`);
}

export function deleteInvoice(uid: string, id: string) {
  inBackground(deleteDoc(doc(invoices(uid), id)), `delete invoice ${id}`);
}

// ── Clients ──────────────────────────────────────────────────────────────

export function watchClients(uid: string, onChange: (clients: Client[]) => void, onError: (e: unknown) => void): Unsubscribe {
  return onSnapshot(clients(uid), (s) => onChange(sortClients(parseDocs(s.docs, clientFromJson))), onError);
}

export function saveClient(uid: string, client: Client) {
  inBackground(setDoc(doc(clients(uid), client.id), clientToJson(client)), `save client ${client.id}`);
}

export function deleteClient(uid: string, id: string) {
  inBackground(deleteDoc(doc(clients(uid), id)), `delete client ${id}`);
}

// ── Saved items ("My items") ─────────────────────────────────────────────

export function watchItems(uid: string, onChange: (items: SavedItem[]) => void, onError: (e: unknown) => void): Unsubscribe {
  return onSnapshot(items(uid), (s) => onChange(sortSavedItems(parseDocs(s.docs, savedItemFromJson))), onError);
}

export function saveItem(uid: string, item: SavedItem) {
  inBackground(setDoc(doc(items(uid), item.id), savedItemToJson(item)), `save item ${item.id}`);
}

export function deleteItem(uid: string, id: string) {
  inBackground(deleteDoc(doc(items(uid), id)), `delete item ${id}`);
}

// ── Plan ─────────────────────────────────────────────────────────────────

/** The plan server code wrote to users/{uid}; free until it says otherwise. */
export function watchPlan(uid: string, onChange: (plan: Plan) => void): Unsubscribe {
  return onSnapshot(
    doc(firestore(), 'users', uid),
    (s) => onChange(planFromUserRecord(s.data())),
    (error) => {
      console.warn('Could not read the plan', error);
      onChange(FREE_PLAN);
    },
  );
}

// ── Public invoice pages ─────────────────────────────────────────────────

export async function publishPublicInvoice(page: { id: string } & Record<string, unknown>): Promise<void> {
  await setDoc(doc(firestore(), 'publicInvoices', page.id), page);
}

export function removePublicInvoice(id: string) {
  inBackground(deleteDoc(doc(firestore(), 'publicInvoices', id)), 'remove a public invoice page');
}

// ── Deleting everything ──────────────────────────────────────────────────

